import { useState } from "react";
import {
  useCreateMailAccount,
  useUpdateMailAccount,
  useTestMailAccount,
  type MailAccount,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, XCircle, Loader2, ExternalLink, Info } from "lucide-react";
import {
  MAIL_PROVIDERS,
  CUSTOM_PROVIDER_ID,
  detectProvider,
  type MailProvider,
} from "@/lib/mail-providers";

export interface MailAccountPreset {
  id: string;
  label: string;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  hint?: string;
}

// Liste des presets visibles dans le sélecteur — alimentée par le catalogue
// partagé MAIL_PROVIDERS, plus une option « Autre serveur (saisie manuelle) ».
export const PRESETS: MailAccountPreset[] = [
  ...MAIL_PROVIDERS.map((p) => ({
    id: p.id,
    label: p.label,
    imapHost: p.imapHost,
    imapPort: p.imapPort,
    imapSecure: p.imapSecure,
    smtpHost: p.smtpHost,
    smtpPort: p.smtpPort,
    smtpSecure: p.smtpSecure,
    hint: p.hint,
  })),
  {
    id: CUSTOM_PROVIDER_ID,
    label: "Autre serveur (saisie manuelle)",
    imapHost: "",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "",
    smtpPort: 465,
    smtpSecure: true,
  },
];

interface FormState {
  label: string;
  emailAddress: string;
  fromName: string;
  signature: string;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  imapUsername: string;
  imapPassword: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUsername: string;
  smtpPassword: string;
  isDefault: boolean;
  autoAnalyzeFromContains: string;
}

function emptyForm(): FormState {
  return {
    label: "",
    emailAddress: "",
    fromName: "",
    signature: "",
    imapHost: "",
    imapPort: 993,
    imapSecure: true,
    imapUsername: "",
    imapPassword: "",
    smtpHost: "",
    smtpPort: 465,
    smtpSecure: true,
    smtpUsername: "",
    smtpPassword: "",
    isDefault: true,
    autoAnalyzeFromContains: "",
  };
}

function fromAccount(a: MailAccount): FormState {
  return {
    label: a.label,
    emailAddress: a.emailAddress,
    fromName: a.fromName ?? "",
    signature: a.signature ?? "",
    imapHost: a.imapHost,
    imapPort: a.imapPort,
    imapSecure: a.imapSecure,
    imapUsername: a.imapUsername,
    imapPassword: "",
    smtpHost: a.smtpHost,
    smtpPort: a.smtpPort,
    smtpSecure: a.smtpSecure,
    smtpUsername: a.smtpUsername,
    smtpPassword: "",
    isDefault: a.isDefault,
    autoAnalyzeFromContains: (a.autoAnalyzeFromContains ?? []).join(", "),
  };
}

export default function MailAccountForm({
  account,
  onSaved,
  onCancel,
}: {
  account?: MailAccount | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(
    account ? fromAccount(account) : emptyForm(),
  );
  const [presetId, setPresetId] = useState<string>(CUSTOM_PROVIDER_ID);
  // Distingue un preset choisi *à la main* dans le sélecteur d'un preset
  // auto-appliqué par la détection du domaine. Tant que l'utilisateur n'a
  // pas fait de choix manuel, on continue de re-détecter à chaque saisie
  // (ex. il change `prenom@gmail.com` en `prenom@outlook.fr`).
  const [presetSource, setPresetSource] = useState<"auto" | "manual">("auto");

  const create = useCreateMailAccount({
    mutation: {
      onSuccess: () => {
        toast({ title: "Compte mail ajouté" });
        onSaved();
      },
      onError: (err: Error) =>
        toast({ title: "Création échouée", description: err.message, variant: "destructive" }),
    },
  });
  const update = useUpdateMailAccount({
    mutation: {
      onSuccess: () => {
        toast({ title: "Compte mail mis à jour" });
        onSaved();
      },
      onError: (err: Error) =>
        toast({ title: "Mise à jour échouée", description: err.message, variant: "destructive" }),
    },
  });
  const testMutation = useTestMailAccount({
    mutation: {
      onError: (err: Error) =>
        toast({ title: "Test échoué", description: err.message, variant: "destructive" }),
    },
  });

  function setField<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function applyPreset(id: string, source: "auto" | "manual") {
    setPresetId(id);
    setPresetSource(source);
    const p = PRESETS.find((x) => x.id === id);
    if (!p || p.id === CUSTOM_PROVIDER_ID) return;
    setForm((prev) => ({
      ...prev,
      imapHost: p.imapHost,
      imapPort: p.imapPort,
      imapSecure: p.imapSecure,
      smtpHost: p.smtpHost,
      smtpPort: p.smtpPort,
      smtpSecure: p.smtpSecure,
    }));
  }

  /**
   * Met à jour l'adresse e-mail et, tant que l'utilisateur n'a pas
   * sélectionné un preset *à la main*, applique automatiquement la
   * configuration IMAP correspondant au domaine reconnu. Cela permet
   * de passer de gmail.com à outlook.fr sans devoir toucher au
   * sélecteur — la détection reste active jusqu'au premier choix manuel.
   */
  function handleEmailChange(value: string) {
    setField("emailAddress", value);
    if (presetSource === "manual") return;
    const detected = detectProvider(value);
    if (detected) {
      applyPreset(detected.id, "auto");
    } else {
      // Domaine inconnu après une détection précédente (ex. l'utilisateur
      // efface l'adresse) : on retombe sur « custom » sans figer le
      // preset, pour pouvoir re-détecter à la prochaine saisie.
      setPresetId(CUSTOM_PROVIDER_ID);
    }
  }

  // Bloc d'aide étape par étape : affiché dès qu'un provider est détecté
  // (par sélection manuelle ou via le domaine de l'adresse e-mail).
  const helpProvider: MailProvider | null =
    MAIL_PROVIDERS.find((p) => p.id === presetId) ??
    detectProvider(form.emailAddress);

  function buildPayload(forTest: boolean) {
    return {
      label: form.label.trim(),
      emailAddress: form.emailAddress.trim(),
      fromName: form.fromName.trim() || null,
      signature: form.signature.trim() || null,
      isDefault: form.isDefault,
      imapHost: form.imapHost.trim(),
      imapPort: Number(form.imapPort),
      imapSecure: form.imapSecure,
      imapUsername: form.imapUsername.trim() || form.emailAddress.trim(),
      imapPassword: form.imapPassword,
      smtpHost: form.smtpHost.trim(),
      smtpPort: Number(form.smtpPort),
      smtpSecure: form.smtpSecure,
      smtpUsername: form.smtpUsername.trim() || form.emailAddress.trim(),
      smtpPassword: form.smtpPassword || form.imapPassword,
      autoAnalyzeFromContains: form.autoAnalyzeFromContains
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
  }

  function handleTest() {
    const payload = buildPayload(true);
    if (!payload.imapPassword) {
      toast({ title: "Mot de passe requis pour tester", variant: "destructive" });
      return;
    }
    testMutation.mutate({ data: payload });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = buildPayload(false);
    if (!payload.label || !payload.emailAddress) {
      toast({ title: "Libellé et adresse e-mail requis", variant: "destructive" });
      return;
    }
    if (account) {
      const updatePayload = { ...payload };
      if (!updatePayload.imapPassword) {
        // Don't overwrite existing password if blank.
        delete (updatePayload as Partial<typeof updatePayload>).imapPassword;
        delete (updatePayload as Partial<typeof updatePayload>).smtpPassword;
      }
      update.mutate({ id: account.id, data: updatePayload });
    } else {
      if (!payload.imapPassword) {
        toast({ title: "Mot de passe requis", variant: "destructive" });
        return;
      }
      create.mutate({ data: payload });
    }
  }

  const presetHint = PRESETS.find((p) => p.id === presetId)?.hint;
  const testResult = testMutation.data;

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="mail-account-form">
      {!account ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Préréglage</Label>
            <Select
              value={presetId}
              onValueChange={(v) => applyPreset(v, "manual")}
            >
              <SelectTrigger data-testid="select-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {presetHint ? (
              <p className="text-xs text-muted-foreground">{presetHint}</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="account-label">Libellé du compte</Label>
            <Input
              id="account-label"
              value={form.label}
              onChange={(e) => setField("label", e.target.value)}
              placeholder="Mon Gmail pro"
              data-testid="input-label"
            />
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="account-label">Libellé</Label>
          <Input
            id="account-label"
            value={form.label}
            onChange={(e) => setField("label", e.target.value)}
          />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="email-address">Adresse e-mail</Label>
          <Input
            id="email-address"
            type="email"
            value={form.emailAddress}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="moi@domaine.fr"
            data-testid="input-email"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="from-name">Nom affiché</Label>
          <Input
            id="from-name"
            value={form.fromName}
            onChange={(e) => setField("fromName", e.target.value)}
            placeholder="Prénom Nom"
          />
        </div>
      </div>

      {helpProvider?.help ? (
        <div
          className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm space-y-3"
          data-testid="provider-help"
        >
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold text-foreground">
                {helpProvider.help.headline}
              </p>
              {helpProvider.help.intro ? (
                <p className="text-muted-foreground text-xs leading-relaxed">
                  {helpProvider.help.intro}
                </p>
              ) : null}
            </div>
          </div>
          <ol className="space-y-2 list-decimal list-inside text-xs leading-relaxed">
            {helpProvider.help.steps.map((s) => (
              <li key={s.title}>
                <span className="font-medium text-foreground">{s.title}.</span>{" "}
                <span className="text-muted-foreground">{s.description}</span>
              </li>
            ))}
          </ol>
          {helpProvider.help.docUrl ? (
            <a
              href={helpProvider.help.docUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              {helpProvider.help.docLabel ?? "Documentation officielle"}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : null}
        </div>
      ) : null}

      <fieldset className="border border-border rounded-xl p-4 space-y-3">
        <legend className="text-xs uppercase tracking-wider text-muted-foreground px-2">
          Réception des e-mails (IMAP)
        </legend>
        <p className="text-xs text-muted-foreground -mt-1">
          Le serveur qui reçoit tes e-mails. Pré-rempli automatiquement pour les
          fournisseurs connus — à ne modifier qu'en cas de serveur spécifique.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="imap-host">Serveur</Label>
            <Input
              id="imap-host"
              value={form.imapHost}
              onChange={(e) => setField("imapHost", e.target.value)}
              data-testid="input-imap-host"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="imap-port">Port</Label>
              <Input
                id="imap-port"
                type="number"
                value={form.imapPort}
                onChange={(e) => setField("imapPort", Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="imap-secure" className="text-xs">Sécurité</Label>
              <Select
                value={form.imapSecure ? "yes" : "no"}
                onValueChange={(v) => setField("imapSecure", v === "yes")}
              >
                <SelectTrigger id="imap-secure">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Oui (recommandé)</SelectItem>
                  <SelectItem value="no">Non (STARTTLS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="imap-username">Identifiant</Label>
            <Input
              id="imap-username"
              value={form.imapUsername}
              onChange={(e) => setField("imapUsername", e.target.value)}
              placeholder="Souvent identique à l'e-mail"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="imap-password">Mot de passe</Label>
            <Input
              id="imap-password"
              type="password"
              value={form.imapPassword}
              onChange={(e) => setField("imapPassword", e.target.value)}
              placeholder={account ? "Laisser vide pour conserver" : ""}
              data-testid="input-imap-password"
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="border border-border rounded-xl p-4 space-y-3">
        <legend className="text-xs uppercase tracking-wider text-muted-foreground px-2">
          Envoi des e-mails (SMTP)
        </legend>
        <p className="text-xs text-muted-foreground -mt-1">
          Le serveur utilisé pour envoyer tes e-mails depuis OneNext.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="smtp-host">Serveur</Label>
            <Input
              id="smtp-host"
              value={form.smtpHost}
              onChange={(e) => setField("smtpHost", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="smtp-port">Port</Label>
              <Input
                id="smtp-port"
                type="number"
                value={form.smtpPort}
                onChange={(e) => setField("smtpPort", Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-secure" className="text-xs">Sécurité</Label>
              <Select
                value={form.smtpSecure ? "yes" : "no"}
                onValueChange={(v) => setField("smtpSecure", v === "yes")}
              >
                <SelectTrigger id="smtp-secure">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Oui (recommandé)</SelectItem>
                  <SelectItem value="no">Non (STARTTLS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="smtp-username">Identifiant</Label>
            <Input
              id="smtp-username"
              value={form.smtpUsername}
              onChange={(e) => setField("smtpUsername", e.target.value)}
              placeholder="Souvent identique à l'IMAP"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="smtp-password">Mot de passe</Label>
            <Input
              id="smtp-password"
              type="password"
              value={form.smtpPassword}
              onChange={(e) => setField("smtpPassword", e.target.value)}
              placeholder={
                account ? "Laisser vide pour conserver" : "Identique à l'IMAP par défaut"
              }
            />
          </div>
        </div>
      </fieldset>

      <div className="space-y-2">
        <Label htmlFor="signature">Signature</Label>
        <Textarea
          id="signature"
          value={form.signature}
          onChange={(e) => setField("signature", e.target.value)}
          placeholder="Cordialement, Prénom Nom"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="auto-analyze">
          Analyse IA automatique (expéditeurs contenant)
        </Label>
        <Input
          id="auto-analyze"
          value={form.autoAnalyzeFromContains}
          onChange={(e) => setField("autoAnalyzeFromContains", e.target.value)}
          placeholder="@boss.com, factures@, support@"
        />
        <p className="text-xs text-muted-foreground">
          Séparé par des virgules. Les emails dont l'expéditeur correspond seront
          analysés automatiquement à la prochaine synchro.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <input
          id="is-default"
          type="checkbox"
          checked={form.isDefault}
          onChange={(e) => setField("isDefault", e.target.checked)}
          className="w-4 h-4"
        />
        <Label htmlFor="is-default" className="text-sm font-normal">
          Compte par défaut pour l'envoi
        </Label>
      </div>

      {testResult ? (
        <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm space-y-1">
          <div className="flex items-center gap-2">
            {testResult.imap.ok ? (
              <CheckCircle2 className="w-4 h-4 text-blue-600" />
            ) : (
              <XCircle className="w-4 h-4 text-destructive" />
            )}
            IMAP : {testResult.imap.ok ? "OK" : testResult.imap.error}
          </div>
          <div className="flex items-center gap-2">
            {testResult.smtp.ok ? (
              <CheckCircle2 className="w-4 h-4 text-blue-600" />
            ) : (
              <XCircle className="w-4 h-4 text-destructive" />
            )}
            SMTP : {testResult.smtp.ok ? "OK" : testResult.smtp.error}
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button type="submit" disabled={create.isPending || update.isPending} data-testid="button-save-account">
          {(create.isPending || update.isPending) && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          {account ? "Enregistrer" : "Ajouter le compte"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleTest}
          disabled={testMutation.isPending}
          data-testid="button-test-connection"
        >
          {testMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Tester la connexion
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
